import { Anchor, ListItemLayout, ListLayout, RadioButtonCard, search } from '@keplerco/core';
import React, { Fragment, useEffect, useState } from 'react';
import { BaseSearchRequest } from '../../../../models';
import { useAppActions, useAppState } from '../../../../overmind';
import classNames from 'classnames';
import { SearchList } from '../../../../components/lists/search-list';
import { ListItem } from '../../../../components/lists/list-item';
import { ActivationStatus, AssessmentAssigneeType, OrganizationLevelType } from '../../../../enums';
import { AssessmentAssigneesResponse } from '../../../../overmind/actions';


const pageSize = 6;

enum ErrorMessage {
  NoEntities = 'At least one entity must be selected',
}

interface AssessmentsWizardEntity {
  slug: string;
  name: string;
  email?: string;
  disabled?: boolean;
}

interface AssessmentsWizardEntities {
  totalPages: number;
  entities: AssessmentsWizardEntity[];
}

export function AssessmentEntitiesWidget({ assignees, onSaveAndContinue }: { assignees: AssessmentAssigneesResponse | undefined; onSaveAndContinue: () => void }): JSX.Element {
  const actions = useAppActions();
  const { companyVariables, user, permissions } = useAppState();

  const [selectedEntityType, setSelectedEntityType] = useState<AssessmentAssigneeType>(AssessmentAssigneeType.Company);

  const [loading, setLoading] = useState<boolean>(false);
  const [request, setRequest] = useState<BaseSearchRequest>({
    companySlug: companyVariables.slug,
    page: 1,
    pageSize,
    sortAscending: true,
  });
  const [entities, setEntities] = useState<AssessmentsWizardEntities>();
  const [searchedEntities, setSearchedEntities] = useState<AssessmentsWizardEntities>();
  const [selectedEntities, setSelectedEntities] = useState<string[]>();
  const [allowedCompany, setAllowedCompany] = useState<boolean>();
  const [dirty, setDirty] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<ErrorMessage>();

  // Set whether they are allowed to assign to company
  useEffect(() => {
    setAllowedCompany(!!user?.isSystemAdmin ||
      permissions?.analytics?.organizationLevels?.some(perm => perm.organizationLevel === OrganizationLevelType.Company));
  }, []);

  // Initialize selected entity type from assignees when component mounts or assignees change
  useEffect(() => {
    if (!!assignees && assignees.assessmentSlug) {
      setSelectedEntityType(assignees.assigneeType);
    }
  }, [assignees]);

  // Update selected entities when entity type or assignees change
  useEffect(() => {
    if (!!assignees && assignees.assessmentSlug) {
      switch (selectedEntityType) {
        case AssessmentAssigneeType.Department: {
          setSelectedEntities(assignees.departments?.map(department => department.slug) || []);
          break;
        }

        case AssessmentAssigneeType.Team: {
          setSelectedEntities(assignees.teams?.map(team => team.slug) || []);
          break;
        }

        case AssessmentAssigneeType.Learner: {
          setSelectedEntities(assignees.assignees?.map(user => user.learnerSlug) || []);
          break;
        }

        default: {
          setSelectedEntities([]);
          break;
        }
      }
    } else {
      setSelectedEntities([]);
    }
  }, [assignees, selectedEntityType]);

  useEffect(() => {
    async function getDepartments() {
      setLoading(true);
      const response = await actions.getDepartments(companyVariables.slug!);
      const temp: AssessmentsWizardEntities | undefined = !!response
        ? {
          totalPages: response.length ? 1 : 0,
          entities: response.map(department => ({
            slug: department.slug,
            name: department.name,
          })),
        }
        : undefined;
      setEntities(temp);
      setSearchedEntities(temp);
      setLoading(false);
    }

    async function getTeams() {
      setLoading(true);
      const response = await actions.getTeams(companyVariables.slug!);
      const temp: AssessmentsWizardEntities | undefined = !!response
        ? {
          totalPages: response.length ? 1 : 0,
          entities: response.map(team => ({
            slug: team.slug!,
            name: team.teamName,
          })),
        }
        : undefined;
      setEntities(temp);
      setSearchedEntities(temp);
      setLoading(false);
    }

    async function getPeople() {
      setLoading(true);
      const response = await actions.searchPeople({ ...request });

      if (!response) {
        setEntities(undefined);
        setSearchedEntities(undefined);
        setLoading(false);
        return;
      }

      const temp: AssessmentsWizardEntities = {
        totalPages: response.totalPages,
        entities: response.employees.filter(employee => employee.activationStatus !== ActivationStatus.Archived)
          .map(employee => ({
            slug: employee.learnerSlug,
            name: `${employee.firstName} ${employee.lastName}`,
            email: employee.email,
          })),
      };

      setEntities(temp);
      setSearchedEntities(temp);
      setLoading(false);
    }

    if (selectedEntityType === AssessmentAssigneeType.Department) getDepartments();
    if (selectedEntityType === AssessmentAssigneeType.Team) getTeams();
    if (selectedEntityType === AssessmentAssigneeType.Learner) getPeople();
  }, [request, selectedEntityType, companyVariables.slug]);

  useEffect(() => {
    if (selectedEntityType !== AssessmentAssigneeType.Company && !selectedEntities?.length) return setErrorMessage(ErrorMessage.NoEntities);
    setErrorMessage(undefined);
  }, [selectedEntityType, selectedEntities]);

  return (
    <Fragment>
      <div style={{ marginBottom: 15 }}>
        <ListLayout wrap>
          {allowedCompany &&
            <ListItemLayout>
              <RadioButtonCard
                id={'Company'}
                checked={selectedEntityType === AssessmentAssigneeType.Company}
                onClick={() => {
                  if (selectedEntityType === AssessmentAssigneeType.Company) return;
                  setSelectedEntities([]);
                  setSelectedEntityType(AssessmentAssigneeType.Company);
                  setDirty(false);
                }}
              >
                <div className="card">
                  <label className="toggleCardLabel" htmlFor={'Company'}>
                    Company
                  </label>
                </div>
              </RadioButtonCard>
            </ListItemLayout>
          }
          <ListItemLayout>
            <RadioButtonCard
              id={'Department'}
              checked={selectedEntityType === AssessmentAssigneeType.Department}
              onClick={() => {
                if (selectedEntityType === AssessmentAssigneeType.Department) return;
                setSelectedEntities([]);
                setSelectedEntityType(AssessmentAssigneeType.Department);
                setDirty(false);
              }}
            >
              <div className="card">
                <label className="toggleCardLabel" htmlFor={'Department'}>
                  Department
                </label>
              </div>
            </RadioButtonCard>
          </ListItemLayout>
          <ListItemLayout>
            <RadioButtonCard
              id={'Team'}
              checked={selectedEntityType === AssessmentAssigneeType.Team}
              onClick={() => {
                if (selectedEntityType === AssessmentAssigneeType.Team) return;
                setSelectedEntities([]);
                setSelectedEntityType(AssessmentAssigneeType.Team);
                setDirty(false);
              }}
            >
              <div className="card">
                <label className="toggleCardLabel" htmlFor={'Team'}>
                  Team
                </label>
              </div>
            </RadioButtonCard>
          </ListItemLayout>
          <ListItemLayout>
            <RadioButtonCard
              id={'Person'}
              checked={selectedEntityType === AssessmentAssigneeType.Learner}
              onClick={() => {
                if (selectedEntityType === AssessmentAssigneeType.Learner) return;
                setSelectedEntities([]);
                setSelectedEntityType(AssessmentAssigneeType.Learner);
                setDirty(false);
              }}
            >
              <div className="card">
                <label className="toggleCardLabel" htmlFor={'Person'}>
                  Person
                </label>
              </div>
            </RadioButtonCard>
          </ListItemLayout>
        </ListLayout>
      </div>

      {selectedEntityType !== AssessmentAssigneeType.Company && (
        <div className="card" style={{ marginBottom: 15 }}>
          <SearchList
            loading={loading}
            onInput={query => {
              if (selectedEntityType === AssessmentAssigneeType.Learner) {
                setRequest(currentState => ({ ...currentState, search: query, page: 1 }));
                return;
              }

              if (!entities) return;
              const temp = search(entities.entities, query, 'name');
              setSearchedEntities({
                totalPages: temp.length ? 1 : 0,
                entities: temp,
              });
            }}
            paging={{
              activePageNumber: request.page ?? 1,
              pageCount: entities?.totalPages ?? 0,
              onPageChange: page => setRequest(currentState => ({ ...currentState, page })),
            }}
          >
            {searchedEntities?.entities.map(entity => {
              const selected = selectedEntities?.includes(entity.slug!);
              return (
                <ListItem
                  key={entity.slug}
                  selected={!!selected}
                  disabled={entity.disabled}
                  onClick={() => {
                    if (selected) {
                      setSelectedEntities(currentState => {
                        if (!currentState) return;
                        let nextState: string[] = structuredClone(currentState);
                        nextState = nextState.filter(selectedEntity => selectedEntity !== entity.slug);
                        return nextState;
                      });
                    } else {
                      setSelectedEntities(currentState => {
                        if (!currentState) return;
                        const nextState = structuredClone(currentState);
                        nextState.push(entity.slug!);
                        return nextState;
                      });
                    }
                  }}
                >
                  {selectedEntityType === AssessmentAssigneeType.Learner ? (
                    <div style={{ display: 'grid', gap: 15, alignItems: 'center', gridTemplateColumns: '1fr 1fr' }}>
                      <span style={{ color: 'var(--accent-2)' }}>{entity.name}</span>

                      <small>{entity.email}</small>
                    </div>
                  ) : (
                    <span style={{ color: 'var(--accent-2)' }}>{entity.name}</span>
                  )}
                </ListItem>
              );
            })}
          </SearchList>
        </div>
      )}

      <div className={classNames('formErrorMessage', { invisible: !dirty || !errorMessage })}>{errorMessage}&nbsp;</div>

      <div style={{ marginTop: 15, display: 'flex', flexDirection: 'row', justifyContent: 'flex-end' }}>
        <Anchor
          arrow
          onClick={async () => {
            setDirty(true);

            if (!assignees?.assessmentSlug) return;
            if (!!errorMessage) return;

            setLoading(true);

            switch (selectedEntityType) {
              case AssessmentAssigneeType.Company: {
                await actions.updateAssessmentAssignees({
                  includeLearnersWithNoDepartment: false,
                  learnerSlugs: [],
                  teamSlugs: [],
                  departmentSlugs: [],
                  assigneeType: AssessmentAssigneeType.Company,
                  assessmentSlug: assignees.assessmentSlug
                });
                break;
              }

              case AssessmentAssigneeType.Department: {
                await actions.updateAssessmentAssignees({
                  includeLearnersWithNoDepartment: false,
                  learnerSlugs: [],
                  teamSlugs: [],
                  departmentSlugs: selectedEntities!,
                  assigneeType: AssessmentAssigneeType.Department,
                  assessmentSlug: assignees.assessmentSlug
                });
                break;
              }

              case AssessmentAssigneeType.Team: {
                await actions.updateAssessmentAssignees({
                  includeLearnersWithNoDepartment: false,
                  learnerSlugs: [],
                  teamSlugs: selectedEntities!,
                  departmentSlugs: [],
                  assigneeType: AssessmentAssigneeType.Team,
                  assessmentSlug: assignees.assessmentSlug
                });
                break;
              }

              case AssessmentAssigneeType.Learner: {
                await actions.updateAssessmentAssignees({
                  includeLearnersWithNoDepartment: false,
                  learnerSlugs: selectedEntities!,
                  teamSlugs: [],
                  departmentSlugs: [],
                  assigneeType: AssessmentAssigneeType.Learner,
                  assessmentSlug: assignees.assessmentSlug
                });
                break;
              }

              default: {
                break;
              }
            }

            setLoading(false);

            onSaveAndContinue();
          }}
        >
          Save & Continue
        </Anchor>
      </div>
    </Fragment>
  );
}
